import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { ReactNode } from "react";
import { useTheme } from "../../context/ThemeContext";

/**
 * A choice between a few options that all stay in view: the chosen one sits
 * raised on a quiet track. For picking one of several, where a switch would
 * only say on or off.
 */
export function SegmentedControl<Value extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  fullWidth = false,
  disabled = false,
}: {
  value: Value;
  // a title says in a tooltip what choosing the option means
  options: readonly { value: Value; label: ReactNode; title?: string }[];
  onChange: (newValue: Value) => void;
  ariaLabel?: string;
  /** The options share the width of what the control stands in. */
  fullWidth?: boolean;
  /** The choice is shown and cannot be changed. */
  disabled?: boolean;
}) {
  const { theme } = useTheme();

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      fullWidth={fullWidth}
      disabled={disabled}
      value={value}
      aria-label={ariaLabel}
      // a re-click on the selected button reports null: this is a choice, so
      // that click keeps the selection
      onChange={(_, newValue: Value | null) => {
        if (newValue !== null) onChange(newValue);
      }}
      sx={{
        bgcolor: theme.palette.action.hover,
        borderRadius: 1.75,
        p: "3px",
        "& .MuiToggleButton-root": {
          border: "none",
          borderRadius: `${theme.shape.borderRadius}px !important`,
          textTransform: "none",
          fontWeight: 600,
          color: "text.secondary",
          px: 1.5,
          py: 0.5,
          "&.Mui-selected, &.Mui-selected:hover": {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: theme.shadows[1],
          },
        },
      }}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          title={option.title}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
